import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowUpRight, ShieldCheck, BadgeCheck, Globe } from "lucide-react";
import { BUSINESS, RETROFITIQ_LOGO } from "@/data/content";
import { TEST_IDS } from "@/constants/testIds";
import { openCookieSettings } from "@/components/CookieConsent";

const COLUMNS = [
  {
    title: "Diagnostics",
    links: [
      ["Building Performance Diagnostics", "/building-performance-diagnostics"],
      ["Home Health Diagnostics", "/services/home-health-diagnostics"],
      ["Thermal Imaging", "/services/thermal-imaging"],
      ["Blower Door Testing", "/services/blower-door-testing"],
      ["Damp, Moisture & Condensation", "/services/damp-moisture-condensation"],
    ],
  },
  {
    title: "Performance",
    links: [
      ["Insulation & Retrofit", "/services/insulation-retrofit"],
      ["External Wall Insulation & Cladding", "/services/external-wall-insulation-cladding"],
      ["MVHR & Ventilation", "/services/mvhr-ventilation"],
      ["Heat Pump Readiness", "/services/heat-pump-readiness"],
      ["Soundproofing & Acoustics", "/services/soundproofing-acoustics"],
    ],
  },
  {
    title: "Passive House",
    links: [
      ["Building Performance Engineering", "/building-performance-engineering"],
      ["Quality Assurance", "/building-performance-quality-assurance"],
      ["Engineering & Design Services", "/engineering-design-services"],
      ["Building Physics Assessment", "/building-physics-assessment"],
      ["Passive House Design", "/services/passive-house-design"],
      ["EnerPHit Retrofit Design", "/services/enerphit-retrofit-design"],
      ["PHPP Energy Modelling", "/services/phpp-energy-modelling"],
      ["Certification Support", "/services/passive-house-certification-support"],
    ],
  },
  {
    title: "Academy",
    links: [
      ["Retrofit IQ Academy", "/learn"],
      ["Passive House Guide", "/learn/guides/passive-house"],
      ["Airtightness Guide", "/learn/guides/airtightness"],
      ["Building Physics Guide", "/learn/guides/building-physics"],
      ["Thermal Imaging Guide", "/learn/guides/thermal-imaging"],
      ["Ventilation & IAQ Guide", "/learn/guides/ventilation"],
      ["Retrofit & Insulation Guide", "/learn/guides/retrofit"],
      ["Soundproofing Guide", "/learn/guides/soundproofing"],
      ["FAQ", "/faq"],
      ["Why Damp Treatments Fail", "/learn/why-damp-treatments-fail"],
      ["Project Map", "/projects"],
      ["Self-Assessment", "/self-assessment"],
      ["Retrofit IQ Investigations", "/investigations"],
      ["Gallery", "/gallery"],
      ["Reviews", "/reviews"],
      ["Areas Covered", "/areas"],
    ],
  },
  {
    title: "Company",
    links: [
      ["About George Sora", "/about"],
      ["Who We Help", "/professionals"],
      ["For Architects", "/professionals/architects"],
      ["For Landlords", "/professionals/landlords"],
      ["Gallery", "/gallery"],
      ["Reviews", "/reviews"],
      ["FAQ", "/faq"],
      ["Contact", "/contact"],
    ],
  },
];

export default function Footer() {
  return (
    <footer data-testid={TEST_IDS.footer.container} className="bg-[#111827] text-slate-300">
      {/* Top trust strip — green/red brand bands */}
      <div className="bg-[#0f172a] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          <div className="flex items-center gap-2.5 text-slate-200">
            <span className="w-8 h-8 grid place-items-center bg-[#16A34A]/15 text-[#16A34A]"><BadgeCheck className="w-4 h-4" /></span>
            <span className="leading-tight">
              <span className="block font-semibold">Certified Passive House Designer</span>
              <span className="block text-[10px] font-mono uppercase tracking-widest text-slate-500">PHI Accredited</span>
            </span>
          </div>
          <div className="flex items-center gap-2.5 text-slate-200">
            <span className="w-8 h-8 grid place-items-center bg-[#DC2626]/15 text-[#DC2626]"><ShieldCheck className="w-4 h-4" /></span>
            <span className="leading-tight">
              <span className="block font-semibold">Fully Insured</span>
              <span className="block text-[10px] font-mono uppercase tracking-widest text-slate-500">Public Liability Cover</span>
            </span>
          </div>
          <div className="flex items-center gap-2.5 text-slate-200">
            <span className="w-8 h-8 grid place-items-center bg-[#16A34A]/15 text-[#16A34A]"><MapPin className="w-4 h-4" /></span>
            <span className="leading-tight">
              <span className="block font-semibold">London · Essex · Kent · Surrey · Herts</span>
              <span className="block text-[10px] font-mono uppercase tracking-widest text-slate-500">On-site Diagnostics</span>
            </span>
          </div>
          <div className="flex items-center gap-2.5 text-slate-200">
            <span className="w-8 h-8 grid place-items-center bg-[#DC2626]/15 text-[#DC2626]"><Globe className="w-4 h-4" /></span>
            <span className="leading-tight">
              <span className="block font-semibold">retrofitiq.co.uk</span>
              <span className="block text-[10px] font-mono uppercase tracking-widest text-slate-500">Building Performance</span>
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3">
              <img src={RETROFITIQ_LOGO.src} alt={RETROFITIQ_LOGO.alt} className="w-14 h-14 object-contain" loading="lazy" width="56" height="56" />
              <div>
                <div className="font-heading font-semibold text-white text-lg">
                  <span className="text-[#DC2626]">Retro</span><span className="text-[#16A34A]">Fit</span><span className="text-[#DC2626]">IQ</span>
                </div>
                <div className="overline text-slate-500">Building Performance Diagnostics</div>
              </div>
            </div>
            <p className="mt-6 text-sm text-slate-400 leading-relaxed max-w-md">
              One Company. One Process. One Point of Responsibility. Diagnostics-led building
              performance, retrofit and envelope-system solutions for homeowners, landlords,
              architects, property managers, housing associations and developers.
            </p>
            <div className="mt-4 text-xs text-slate-500 leading-relaxed max-w-md">
              RetrofitIQ is operated by G.A.S General Building Services Ltd.
            </div>
            <div className="mt-8 space-y-3 text-sm">
              <a data-testid={TEST_IDS.footer.phone} href={BUSINESS.phoneHref} className="flex items-center gap-3 text-slate-200 hover:text-white">
                <span className="w-8 h-8 grid place-items-center bg-[#16A34A]/15 text-[#16A34A]"><Phone className="w-3.5 h-3.5" /></span>
                <span className="font-mono font-semibold">{BUSINESS.phone}</span>
              </a>
              <a data-testid={TEST_IDS.footer.email} href={BUSINESS.emailHref} className="flex items-center gap-3 text-slate-200 hover:text-white break-all">
                <span className="w-8 h-8 grid place-items-center bg-[#DC2626]/15 text-[#DC2626]"><Mail className="w-3.5 h-3.5" /></span>
                {BUSINESS.email}
              </a>
              <a data-testid="footer-website" href="https://retrofitiq.co.uk" className="flex items-center gap-3 text-slate-200 hover:text-white">
                <span className="w-8 h-8 grid place-items-center bg-[#16A34A]/15 text-[#16A34A]"><Globe className="w-3.5 h-3.5" /></span>
                retrofitiq.co.uk
              </a>
              <div className="flex items-start gap-3 text-slate-400">
                <span className="w-8 h-8 grid place-items-center bg-white/5 text-slate-300 shrink-0"><MapPin className="w-3.5 h-3.5" /></span>
                <div>
                  <div>London · Essex · Kent · Surrey · Hertfordshire</div>
                  <div className="mt-0.5 text-[11px] text-slate-500">Registered office: {BUSINESS.address}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-10">
            {COLUMNS.map((col) => (
              <div key={col.title}>
                <div className="overline text-[#16A34A] mb-5">{col.title}</div>
                <ul className="space-y-3 text-sm">
                  {col.links.map(([label, to]) => (
                    <li key={to}>
                      <Link to={to} className="text-slate-400 hover:text-white inline-flex items-center gap-1.5 group">
                        {label}
                        <ArrowUpRight className="w-3.5 h-3.5 opacity-0 -translate-y-0.5 group-hover:opacity-100 transition" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Service area links — local SEO + visitor navigation */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="font-mono uppercase tracking-widest text-[10px] text-slate-500 mb-3">London service areas</div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-400">
            <Link to="/areas/london" className="hover:text-white">London</Link>
            <Link to="/areas/east-london" className="hover:text-white">East London</Link>
            <Link to="/areas/north-london" className="hover:text-white">North London</Link>
            <Link to="/areas/south-london" className="hover:text-white">South London</Link>
            <Link to="/areas/west-london" className="hover:text-white">West London</Link>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-slate-500">
          <div className="font-mono">
            © {new Date().getFullYear()} RetrofitIQ · G.A.S General Building Services Ltd. All rights reserved.
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link to="/privacy-policy" data-testid="footer-privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/cookie-policy" data-testid="footer-cookies" className="hover:text-white">Cookie Policy</Link>
            <button type="button" onClick={openCookieSettings} data-testid="footer-cookie-settings" className="hover:text-white">Cookie Settings</button>
            <Link to="/faq" className="hover:text-white">FAQ</Link>
          </div>
        </div>
        <div className="mt-6 font-mono uppercase tracking-widest text-[10px] text-slate-500 text-center md:text-left">
          <span className="text-[#16A34A]">Investigate</span> · Diagnose · Design · Remediate · <span className="text-[#16A34A]">Verify</span>
        </div>
      </div>
    </footer>
  );
}
